import React, { useEffect } from 'react';
import { Modal, Form, Input, Button, message, Space } from 'antd';
import { useIntl } from 'react-intl';
import {
    useCreateCustomBrandChartMutation,
    useUpdateCustomBrandChartMutation,
} from '../../services/nestjs-base-api';
import { ICustomBrandChart } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';

interface CustomChartModalProps {
    visible: boolean;
    chart: ICustomBrandChart | null;
    brandHash: string;
    onClose: () => void;
    onSuccess?: (chartId?: number) => void;
}

interface CustomChartFormData {
    chartName: string;
}

const CustomChartModal: React.FC<CustomChartModalProps> = ({
    visible,
    chart,
    brandHash,
    onClose,
    onSuccess,
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [createChart, { isLoading: isCreating }] = useCreateCustomBrandChartMutation();
    const [updateChart, { isLoading: isUpdating }] = useUpdateCustomBrandChartMutation();

    const isEditMode = Boolean(chart);
    const isLoading = isCreating || isUpdating;

    useEffect(() => {
        if (chart && visible) {
            form.setFieldsValue({
                chartName: chart.chartName,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [chart, visible, form]);

    const handleSubmit = async (values: CustomChartFormData) => {
        if (!user?.jwtToken) return;

        try {
            if (isEditMode && chart) {
                // Update existing chart name only
                await updateChart({
                    jwtToken: user.jwtToken,
                    id: chart.id,
                    brandHash,
                    data: {
                        chartName: values.chartName,
                        selectedMetrics: chart.selectedMetrics, // Keep existing metrics
                    },
                }).unwrap();

                message.success(
                    intl.formatMessage(
                        { id: 'customCharts.update.success' },
                        { name: values.chartName }
                    )
                );
            } else {
                // Create new chart with empty metrics
                const newChart = await createChart({
                    jwtToken: user.jwtToken,
                    data: {
                        chartName: values.chartName,
                        selectedMetrics: [], // Start with empty metrics
                        brandHash,
                    },
                }).unwrap();

                message.success(
                    intl.formatMessage(
                        { id: 'customCharts.create.success' },
                        { name: values.chartName }
                    )
                );

                form.resetFields();
                onClose();
                onSuccess?.(newChart.id);
                return;
            }

            form.resetFields();
            onClose();
            onSuccess?.();
        } catch (error: any) {
            console.error('Failed to save custom chart:', error);

            // Handle specific error messages
            if (error?.data?.message) {
                message.error(error.data.message);
            } else {
                message.error(
                    intl.formatMessage({
                        id: isEditMode
                            ? 'customCharts.update.error'
                            : 'customCharts.create.error',
                    })
                );
            }
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            title={intl.formatMessage({
                id: isEditMode
                    ? 'customCharts.modal.edit.title'
                    : 'customCharts.modal.create.title',
            })}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={500}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ marginTop: 24 }}
            >
                <Form.Item
                    name="chartName"
                    label={intl.formatMessage({ id: 'customCharts.modal.chartName.label' })}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({
                                id: 'customCharts.modal.chartName.required',
                            }),
                        },
                        {
                            min: 2,
                            message: intl.formatMessage({
                                id: 'customCharts.modal.chartName.minLength',
                            }),
                        },
                        {
                            max: 255,
                            message: intl.formatMessage({
                                id: 'customCharts.modal.chartName.maxLength',
                            }),
                        },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({
                            id: 'customCharts.modal.chartName.placeholder',
                        })}
                        size="large"
                        autoFocus
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={handleCancel} size="large">
                            {intl.formatMessage({ id: 'common.cancelButton' })}
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isLoading}
                            size="large"
                        >
                            {intl.formatMessage({
                                id: isEditMode
                                    ? 'customCharts.modal.edit.submit'
                                    : 'customCharts.modal.create.submit',
                            })}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CustomChartModal;
